import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ScholarshipApplicationApiService } from '../../core/api-services/scholarship-application-api.service';
import { StudentApplication } from '../../core/models/student-application';
import { StudentDocument } from '../../core/models/student-document';
import { AwardApiService } from '../../core/api-services/award-api.service';
import { PaginationComponent } from '../../shared/pagination/pagination';
import { FlightRequestsPage } from '../flight-requests/flight-requests';
import { StudentClaimsComponent } from './claims/claims';
import { StudentExpensesComponent } from './expenses/expenses';
import { StudentProgressReportsComponent } from './progress-reports/progress-reports';
import { StudentEventsComponent } from './events/events';
import { ScholarshipAwardSummary, StudentDashboard } from '../../core/models/scholarship-award';

@Component({
  selector: 'app-student',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PaginationComponent,
    FlightRequestsPage,
    StudentClaimsComponent,
    StudentExpensesComponent,
    StudentProgressReportsComponent,
    StudentEventsComponent,
  ],
  templateUrl: './student.html',
  styleUrl: './student.scss',
})
export class StudentComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly applicationsApi = inject(ScholarshipApplicationApiService);
  private readonly awardApi = inject(AwardApiService);

  currentDate = new Date();
  activeSection = 'dashboard';
  applications: StudentApplication[] = [];
  isLoadingApplications = false;
  applicationsError = '';
  documents: StudentDocument[] = [];
  isLoadingDocuments = false;
  documentsError = '';
  selectedApplication: StudentApplication | null = null;
  isApplicationDetailOpen = false;
  currentPage = 1;
  pageSize = 5;
  readonly pageSizeOptions = [5, 10, 20, 50];
  private appliedSection: string | null = null;

  /** Award-driven portal state: what the student may do and see. */
  dashboard: StudentDashboard | null = null;
  isLoadingDashboard = false;
  dashboardError = '';

  /**
   * Tabs on the Budget page. Budget, expenses and claims are three views of the
   * same funding picture, so they share one menu entry rather than three.
   */
  activeBudgetTab: 'budget' | 'expenses' | 'claims' = 'budget';

  /** A completed award the alumni student is reviewing (read-only). */
  selectedPastAward: ScholarshipAwardSummary | null = null;

  /** The section the URL asked for, re-applied once the award state is known. */
  private requestedSection = 'dashboard';

  /** An explicit ?tab= on the budget page, which wins over the default tab. */
  private requestedTab: 'budget' | 'expenses' | 'claims' | null = null;

  /** Application statuses that mean the application is finished. */
  private static readonly DecidedApplicationStatuses = ['APPROVED', 'DECLINED', 'REJECTED', 'CANCELLED'];

  ngOnInit(): void {
    // React to query-param changes so header nav links (e.g. /student?section=applications)
    // update the active section even when the component is already loaded.
    this.route.queryParamMap.subscribe((params) => {
      const section = params.get('section') || 'dashboard';
      const tab = params.get('tab');

      this.requestedSection = section;
      this.requestedTab = tab === 'budget' || tab === 'expenses' || tab === 'claims' ? tab : null;

      this.applySection(section);
    });

    // The dashboard decides which menu items are available, so it is loaded first.
    this.loadDashboard();

    // The application list decides whether the Applications menu item applies,
    // so it is loaded regardless of the section the student landed on.
    this.loadApplications();
  }

  /**
   * Loads the student's award state: whether they are funded (and may therefore
   * use flights, budget and expenses), have completed a scholarship, or are new.
   */
  loadDashboard(): void {
    this.isLoadingDashboard = true;
    this.dashboardError = '';

    this.awardApi.getMyDashboard().subscribe({
      next: (dashboard) => {
        this.dashboard = dashboard;
        this.isLoadingDashboard = false;

        // A deep link to a gated section is retried now that funding is known.
        if (this.requestedSection !== this.activeSection) {
          this.appliedSection = null;
          this.applySection(this.requestedSection);
        }
      },
      error: () => {
        this.dashboardError = 'We could not load your scholarship dashboard. Please try again.';
        this.isLoadingDashboard = false;
      },
    });
  }

  /** True when an active award unlocks flights, budget, expenses and claims. */
  get hasActiveAward(): boolean {
    return !!this.dashboard?.hasActiveAward;
  }

  /** True when the student has been through a scholarship (completed) but is not funded now. */
  get isAlumniView(): boolean {
    return !this.hasActiveAward && !!this.dashboard?.hasCompletedAward;
  }

  get activeAward() {
    return this.dashboard?.activeAward ?? null;
  }

  get pastAwards(): ScholarshipAwardSummary[] {
    return this.dashboard?.pastAwards ?? [];
  }

  /**
   * True when the student has an application that is still being processed.
   * Decided applications need no menu entry — the dashboard and the application
   * history cover them — so the Applications item is hidden when nothing is open.
   */
  get hasOpenApplication(): boolean {
    return this.applications.some((application) => {
      const status = application.applicationStatus?.name?.trim().toUpperCase() ?? '';
      return !StudentComponent.DecidedApplicationStatuses.includes(status);
    });
  }

  selectBudgetTab(tab: 'budget' | 'expenses' | 'claims'): void {
    this.activeBudgetTab = tab;

    // Keep the URL in step so the chosen tab survives a refresh or a shared link.
    void this.router.navigate([], {
      queryParams: { section: 'budget', tab },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  loadApplications(): void {
    this.isLoadingApplications = true;
    this.applicationsError = '';
    this.applicationsApi.getMyApplications().subscribe({
      next: (response: StudentApplication[] | { data?: StudentApplication[] }) => {
        this.applications = Array.isArray(response) ? response : response.data ?? [];
        this.applications.sort((a, b) => new Date(b.applicationDate).getTime() - new Date(a.applicationDate).getTime());
        this.currentPage = 1;
        this.isLoadingApplications = false;
      },
      error: () => {
        this.applicationsError = 'We could not load your applications. Please try again.';
        this.isLoadingApplications = false;
      },
    });
  }

  loadDocuments(): void {
    this.isLoadingDocuments = true;
    this.documentsError = '';
    this.applicationsApi.getMyDocuments().subscribe({
      next: (documents) => {
        this.documents = Array.isArray(documents)
          ? documents
          : (documents as { data?: StudentDocument[] })?.data ?? [];
        this.isLoadingDocuments = false;
      },
      error: () => {
        this.documentsError = 'We could not load your documents. Please try again.';
        this.isLoadingDocuments = false;
      },
    });
  }

  downloadDocument(studentDocument: StudentDocument): void {
    this.applicationsApi.downloadDocument(studentDocument.objectKey).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const anchor = window.document.createElement('a');
        anchor.href = url;
        anchor.download = studentDocument.fileName || 'document';
        window.document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.documentsError = 'We could not download this document. Please try again.';
      },
    });
  }

  openApplicationDetail(application: StudentApplication): void {
    this.selectedApplication = application;
    this.isApplicationDetailOpen = true;
  }

  closeApplicationDetail(): void {
    this.selectedApplication = null;
    this.isApplicationDetailOpen = false;
  }

  get paginatedApplications(): StudentApplication[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.applications.slice(start, start + this.pageSize);
  }

  get totalPages(): number { return Math.max(1, Math.ceil(this.applications.length / this.pageSize)); }
  get showingFrom(): number { return this.applications.length ? (this.currentPage - 1) * this.pageSize + 1 : 0; }
  get showingTo(): number { return Math.min(this.currentPage * this.pageSize, this.applications.length); }
  get latestApplication(): StudentApplication | undefined { return this.applications[0]; }

  changePageSize(): void {
    this.currentPage = 1;
  }

  statusClass(application: StudentApplication): string {
    const status = application.applicationStatus?.name?.toLowerCase() ?? '';
    if (status.includes('approve') || status.includes('fund')) return 'status-approved';
    if (status.includes('reject') || status.includes('declin')) return 'status-rejected';
    return 'status-pending';
  }

  statusLabel(application: StudentApplication): string {
    return application.applicationStatus?.name || 'Submitted';
  }

  showSection(section: string): void {
    // A menu click has no explicit tab, so the target section picks its own and
    // any remembered ?tab= is cleared from the URL.
    this.requestedTab = null;
    this.applySection(section);

    void this.router.navigate([], {
      queryParams: { section, tab: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private applySection(section: string): void {
    // Flights, the budget page and progress reports only exist once a
    // scholarship is awarded, so an unfunded student is sent to the dashboard.
    const gated = ['flights', 'budget', 'expenses', 'claims', 'progress'].includes(section);
    const allowed = this.hasActiveAward || this.isAlumniView;
    let target = gated && !allowed ? 'dashboard' : section;

    // Budget, expenses and claims are tabs on one page, so those sections
    // resolve to the budget page with the matching tab open.
    if (target === 'claims' || target === 'expenses') {
      this.activeBudgetTab = target;
      target = 'budget';
    } else if (target === 'budget') {
      this.activeBudgetTab = this.requestedTab ?? 'budget';
    }

    if (this.appliedSection === target) {
      return;
    }

    this.appliedSection = target;
    this.activeSection = target;

    if (target === 'documents') {
      this.loadDocuments();
    } else if (target === 'applications') {
      this.loadApplications();
    }
  }

  /** Percentage width helper for the budget and duration trackers. */
  progressWidth(percent: number | null | undefined): string {
    return `${Math.min(100, Math.max(0, percent ?? 0))}%`;
  }

  money(value: number | null | undefined, currency: string | null | undefined): string {
    return `${(value ?? 0).toFixed(2)} ${currency ?? ''}`.trim();
  }

  awardStatusClass(status: string): string {
    switch (status) {
      case 'ACTIVE':
        return 'status status-approved';
      case 'COMPLETED':
        return 'status status-booked';
      case 'TERMINATED':
        return 'status status-rejected';
      default:
        return 'status status-pending';
    }
  }

  openPastAward(award: ScholarshipAwardSummary): void {
    this.selectedPastAward = award;
  }

  closePastAward(): void {
    this.selectedPastAward = null;
  }

  startNewApplication(): void {
    this.router.navigate(['/scholarships']);
  }
}
